import * as React from 'react';
import { Check, HelpCircle, X } from 'lucide-react';
import { cn } from '../../../utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../core/popover';

/**
 * How a checked answer slot looks: not checked yet, right, wrong, or left
 * empty. Empty gets its own neutral look so it doesn't read as a mistake.
 */
export type AnswerCheckState = 'idle' | 'right' | 'wrong' | 'empty';

/**
 * Shared look for an inline answer field (dropdown trigger or typed blank):
 * a white rounded box in the reading font, tinted by its checked state.
 * Plain Tailwind (not zine CSS) so it also works inside portals.
 */
export function answerFieldClassName(state: AnswerCheckState = 'idle') {
  return cn(
    'inline-flex h-9 items-center rounded-[10px] border-[1.5px] bg-white px-3 text-[.95rem] font-medium text-[#1A1018] shadow-none transition-colors',
    'hover:border-[#6B4F55] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CF2E2E]/40 focus-visible:ring-offset-0',
    'placeholder:font-normal placeholder:text-[#C29AA4] data-[placeholder]:font-normal data-[placeholder]:text-[#C29AA4]',
    state === 'idle' && 'border-[#E5CFC9]',
    state === 'right' && 'border-[#2E7D5B] bg-[#E7F4EC] text-[#2E7D5B] hover:border-[#2E7D5B]',
    state === 'wrong' && 'border-[#CF2E2E] text-[#CF2E2E] hover:border-[#CF2E2E]',
    state === 'empty' && 'border-dashed border-[#8A7A76] bg-[#F3ECEA]',
  );
}

export interface CorrectionMarkProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  state: Exclude<AnswerCheckState, 'idle'>;
  /** visible text for wrong/empty ("See fix", "Show answer"); aria-label for right */
  label: string;
  /** icon only, with the label as aria-label — for tight spots like matching cards */
  iconOnly?: boolean;
}

/**
 * The tappable mark next to a checked answer: a quiet green tick when right,
 * a red "See fix" pill when wrong, an ink "Show answer" pill when empty.
 * Words on the pill tell students it opens something.
 */
export const CorrectionMark = React.forwardRef<HTMLButtonElement, CorrectionMarkProps>(
  function CorrectionMark({ state, label, iconOnly = false, className, type = 'button', ...rest }, ref) {
    const showText = state !== 'right' && !iconOnly;
    return (
      <button
        ref={ref}
        type={type}
        aria-label={showText ? undefined : label}
        className={cn(
          'inline-flex h-[26px] shrink-0 items-center gap-1 whitespace-nowrap rounded-full border-0 align-middle text-[.78rem] font-semibold leading-none transition hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1018] focus-visible:ring-offset-1',
          state === 'right' && 'bg-[#E7F4EC] px-[7px] text-[#2E7D5B]',
          state === 'wrong' && 'bg-[#CF2E2E] text-white shadow-[0_2px_0_#8E1218]',
          state === 'empty' && 'bg-[#1A1018] text-white',
          state !== 'right' && (showText ? 'pl-[7px] pr-2.5' : 'px-[6px]'),
          className,
        )}
        {...rest}
      >
        {state === 'right' && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        {state === 'wrong' && <X className="h-3.5 w-3.5" strokeWidth={3} />}
        {state === 'empty' && <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.6} />}
        {showText && label}
      </button>
    );
  },
);

export interface AnswerCorrectionLabels {
  /** mark text when wrong, e.g. "See fix" */
  seeFix: string;
  /** mark text when empty, e.g. "Show answer" */
  showAnswer: string;
  /** mark aria-label when right, e.g. "Correct" */
  correctMark: string;
  /** card titles per state */
  rightTitle: string;
  wrongTitle: string;
  emptyTitle: string;
  /** row labels */
  yourAnswer: string;
  correct: string;
  /** close button, e.g. "Got it" */
  close: string;
}

export interface AnswerCorrectionProps {
  state: Exclude<AnswerCheckState, 'idle'>;
  labels: AnswerCorrectionLabels;
  /** what the student gave (omitted/empty when the blank was left empty) */
  yourAnswer?: React.ReactNode;
  /** the expected answer; the row is hidden when unknown */
  correctAnswer?: React.ReactNode;
  /** explanation (reason from the grader, teacher's correction) */
  reason?: React.ReactNode;
  /** show the mark as an icon only (label becomes its aria-label) */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Correction mark + the card it opens: your answer (struck through when
 * wrong), the correct answer, and why. One piece shared by every exercise
 * that checks inline answers.
 */
export function AnswerCorrection({
  state,
  labels,
  yourAnswer,
  correctAnswer,
  reason,
  iconOnly,
  className,
}: AnswerCorrectionProps) {
  const [open, setOpen] = React.useState(false);
  const title =
    state === 'right' ? labels.rightTitle : state === 'wrong' ? labels.wrongTitle : labels.emptyTitle;
  const markLabel =
    state === 'right' ? labels.correctMark : state === 'wrong' ? labels.seeFix : labels.showAnswer;
  const hasCorrect = correctAnswer != null && correctAnswer !== '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <CorrectionMark state={state} label={markLabel} iconOnly={iconOnly} className={className} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(18rem,85vw)] rounded-[14px] border border-[#F0DAD5] bg-white p-4 text-sm leading-relaxed text-[#1A1018] shadow-[0_14px_40px_rgba(26,16,24,.18)]"
        style={{ fontFamily: "'Montserrat', 'Avenir Next', 'Segoe UI', system-ui, sans-serif" }}
      >
        <p className="m-0 text-[.95rem] font-bold">{title}</p>
        <dl className="my-2 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1">
          {state === 'right' ? (
            <>
              <dt className="text-[.7rem] uppercase tracking-[.08em] text-[#756560]">{labels.yourAnswer}</dt>
              <dd className="m-0 font-semibold text-[#2E7D5B]">{yourAnswer}</dd>
            </>
          ) : (
            <>
              <dt className="text-[.7rem] uppercase tracking-[.08em] text-[#756560]">{labels.yourAnswer}</dt>
              <dd
                className={cn(
                  'm-0 font-semibold',
                  state === 'wrong' ? 'text-[#CF2E2E] line-through decoration-2' : 'text-[#756560]',
                )}
              >
                {state === 'wrong' ? yourAnswer : '—'}
              </dd>
              {hasCorrect && (
                <>
                  <dt className="text-[.7rem] uppercase tracking-[.08em] text-[#756560]">{labels.correct}</dt>
                  <dd className="m-0 font-semibold text-[#2E7D5B]">{correctAnswer}</dd>
                </>
              )}
            </>
          )}
        </dl>
        {reason != null && reason !== '' && <div className="mb-3 text-[#6B4F55]">{reason}</div>}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-full border-0 bg-[#1A1018] px-4 py-1.5 text-[.8rem] font-semibold text-white transition hover:bg-[#3a2a33] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CF2E2E]/50"
        >
          {labels.close}
        </button>
      </PopoverContent>
    </Popover>
  );
}
